import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { PagedResponse } from '@bandido/shared/dtos';
import { QueryKeys } from '../common/types';
import { client } from '../common/services';

export enum DataUploadStatus {
  CREATED = 'created',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
}

export interface ClientUploadRecord {
  id: string;
  agencyId: number;
  s3Id: number;
  createdBy: string;
  status: DataUploadStatus;
  createdAt: Date;
  updatedAt: Date;
  completedAt: Date | null;
  createdByFirstName: string | null;
  createdByLastName: string | null;
  s3Filename: string | null;
  s3FileUrl: string | null;
}

export interface CaseUploadRecord {
  id: string;
  agencyId: number;
  workflowId: string;
  s3Id: number;
  createdBy: string;
  status: DataUploadStatus;
  createdAt: Date;
  updatedAt: Date;
  completedAt: Date | null;
  createdByFirstName: string | null;
  createdByLastName: string | null;
  s3Filename: string | null;
  s3FileUrl: string | null;
}

export const useCreateClientUpload = (options?: UseMutationOptions<ClientUploadRecord, AxiosError, FormData>) => {
  return useMutation(
    async (formData: FormData): Promise<ClientUploadRecord> => {
      const response = await client.post('data-uploads/clients', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    options
  );
};

export const useCreateCaseUpload = (options?: UseMutationOptions<CaseUploadRecord, AxiosError, FormData>) => {
  return useMutation(
    async (formData: FormData): Promise<CaseUploadRecord> => {
      const response = await client.post('data-uploads/cases', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    options
  );
};

interface UseGetPaginatedClientUploadsProps {
  agencyId?: number;
  page?: number;
  limit?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

interface UseGetPaginatedCaseUploadsProps {
  agencyId?: number;
  page?: number;
  limit?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetPaginatedClientUploads = (params?: UseGetPaginatedClientUploadsProps) => {
  const { agencyId, page = 1, limit = 50, onErrorCallback } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getPaginatedClientUploads, agencyId, page, limit],
    queryFn: async (): Promise<PagedResponse<ClientUploadRecord> | undefined> => {
      try {
        const result: AxiosResponse<PagedResponse<ClientUploadRecord>> = await client.get(
          `data-uploads/agency/${agencyId}/clients`,
          {
            params: {
              page,
              limit,
            },
          }
        );
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });
};

export const useGetPaginatedCaseUploads = (params?: UseGetPaginatedCaseUploadsProps) => {
  const { agencyId, page = 1, limit = 50, onErrorCallback } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getPaginatedCaseUploads, agencyId, page, limit],
    queryFn: async (): Promise<PagedResponse<CaseUploadRecord> | undefined> => {
      try {
        const result: AxiosResponse<PagedResponse<CaseUploadRecord>> = await client.get(
          `data-uploads/agency/${agencyId}/cases`,
          {
            params: {
              page,
              limit,
            },
          }
        );
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });
};
